import { AfterViewInit, Component, ElementRef, OnDestroy, computed, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentNodeModel } from '../../../../core/models/data/document.model';
import { DocumentNodeType, DocumentPermission } from '../../../../core/enums/data.enums';
import { DocumentService } from '../../../../core/services/document.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions } from '../../../../core/enums/base.enums';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { DOCUMENT_PERMISSION_LABELS } from '../../../../core/constants/display-labels.constants';
import { DocumentUtilities } from '../../../../core/utilities/document.utilities';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';

export enum RenameMode {
    RenameFolder = 'rename-folder',
    RenameFile = 'rename-file',
    CreateFolder = 'create-folder'
}

@Component({
    selector: 'ms-document-rename-modal',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, ReactiveFormsModule, EscCloseDirective],
    templateUrl: './document-rename-modal.component.html'
})
export class DocumentRenameModalComponent extends BaseFormComponent implements AfterViewInit, OnDestroy {

    // Set imperatively by the open*() methods rather than bound as inputs: Bootstrap's show() fires
    // synchronously, before zoneless change detection would propagate inputs, so seeding the form on
    // the show event read stale values. Setting state directly before show keeps it deterministic.
    public target = signal<DocumentNodeModel | undefined>(undefined);
    public mode = signal<RenameMode>(RenameMode.RenameFolder);
    private parentFolderId = signal<number | undefined>(undefined);
    private parentPermission = signal<DocumentPermission | undefined>(undefined);
    private organizationId = signal<number>(1);

    public submitted = output<{ name: string, mode: RenameMode }>();
    public cancelled = output<void>();

    public modalId = DocumentsConstants.RENAME_MODAL_ID();
    public form: FormGroup;
    public errorMessage = signal<string>('');
    public selectedPermission = signal<DocumentPermission>(DocumentPermission.AdminOnly);

    public DocumentPermission = DocumentPermission;

    public isFolderMode = computed<boolean>(() => this.mode() === RenameMode.RenameFolder);
    public isFileMode = computed<boolean>(() => this.mode() === RenameMode.RenameFile);
    public isCreateMode = computed<boolean>(() => this.mode() === RenameMode.CreateFolder);

    // The locked extension (including the dot, e.g. ".pdf") shown beside the editable base name in
    // file-rename mode. Empty for folders/create or extensionless files.
    public fileExtension = signal<string>('');

    // Permission is only choosable when creating a top-level folder (no parent). Subfolders inherit
    // their parent's (ultimately the top-level folder's) permission and can't override it.
    public canChoosePermission = computed<boolean>(() => this.isCreateMode() && this.parentFolderId() === undefined);
    public inheritedPermissionLabel = computed<string>(() => {
        const permission = this.parentPermission();
        return permission ? DOCUMENT_PERMISSION_LABELS[permission] : '';
    });

    public title = computed<string>(() => {
        if (this.isCreateMode()) return 'New folder';
        if (this.isFileMode()) return `Rename file "${this.target()?.name ?? ''}"`;
        return `Rename folder "${this.target()?.name ?? ''}"`;
    });

    public placeholder = computed<string>(() => {
        if (this.isCreateMode()) return 'Untitled folder';
        if (this.isFileMode()) return 'File name';
        return 'New name';
    });

    // Both length constants already reserve headroom for the API's auto-increment " (N)" suffix.
    // File mode additionally subtracts the locked extension, since base + extension share the cap.
    public nameMaxLength = computed<number>(() =>
        this.isFileMode()
            ? Math.max(1, DocumentsConstants.FILE_NAME_MAX_LENGTH() - this.fileExtension().length)
            : DocumentsConstants.FOLDER_NAME_MAX_LENGTH()
    );

    // Suppress the "required" error while the field is focused, so it only surfaces after a blur.
    public isNameFocused = signal<boolean>(false);

    private elementRef = inject(ElementRef<HTMLElement>);
    // Clear any focus/blur churn from the open animation so the field doesn't open pre-touched.
    private readonly onModalShown = (): void => this.form.get('name')?.markAsUntouched();

    constructor(private fb: FormBuilder, private documentService: DocumentService, private bootstrapService: BootstrapService) {
        super();
        this.form = this.fb.group({
            name: ['', [Validators.required]]
        });

        // Sibling-name collisions are not blocked here: the API auto-appends a numeric suffix
        // ("name (1)", "name (2)") and returns the final name, which we surface on success.
    }

    public ngAfterViewInit(): void {
        this.modalElement()?.addEventListener('shown.bs.modal', this.onModalShown);
    }

    public ngOnDestroy(): void {
        this.modalElement()?.removeEventListener('shown.bs.modal', this.onModalShown);
    }

    /** Open the modal to rename a folder or file (mode is chosen from the node's type). */
    public openRename(node: DocumentNodeModel): void {
        this.mode.set(node.type === DocumentNodeType.File ? RenameMode.RenameFile : RenameMode.RenameFolder);
        this.target.set(node);
        this.show();
    }

    /** Open the modal to create a new folder under the given parent. */
    public openCreateFolder(parentFolderId: number | undefined, parentPermission: DocumentPermission | undefined, organizationId: number): void {
        this.mode.set(RenameMode.CreateFolder);
        this.target.set(undefined);
        this.parentFolderId.set(parentFolderId);
        this.parentPermission.set(parentPermission);
        this.organizationId.set(organizationId);
        this.show();
    }

    // Seed the form synchronously from the just-set state, then show — so the field is correct before
    // the modal becomes visible (no reliance on input propagation timing).
    private show(): void {
        this.initializeForm();
        this.bootstrapService.manageModal(`#${this.modalId}`, ModalActions.Show);
    }

    private modalElement(): HTMLElement | null {
        return this.elementRef.nativeElement.querySelector('.modal');
    }

    // Seed the form for the current open: empty for create (with the parent's permission
    // pre-selected), or the target's current name for rename.
    private initializeForm(): void {
        this.errorMessage.set('');
        this.isNameFocused.set(false);
        this.fileExtension.set('');
        if (this.isCreateMode()) {
            this.form.reset({ name: '' });
            this.selectedPermission.set(this.parentPermission() ?? DocumentPermission.AdminOnly);
        } else if (this.isFileMode()) {
            // Edit only the base name; the extension is locked and re-appended on submit.
            const { base, extension } = DocumentUtilities.splitFileName(this.target()?.name ?? '');
            this.fileExtension.set(extension);
            this.form.reset({ name: base });
        } else {
            this.form.reset({ name: this.target()?.name ?? '' });
        }
    }

    public choosePermission(level: DocumentPermission): void {
        this.selectedPermission.set(level);
    }

    public async submit(): Promise<void> {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const inputName = (this.form.value.name as string).trim();
        if (!inputName) {
            this.errorMessage.set(DocumentsMessagingConstants.DOCUMENTS_RENAME_NAME_REQUIRED());
            return;
        }
        const finalName = inputName;
        this.isSubmitting = true;
        this.errorMessage.set('');

        if (this.isCreateMode()) {
            const result = await this.documentService.createFolder({
                organizationId: this.organizationId(),
                parentFolderId: this.parentFolderId(),
                name: finalName,
                permission: this.selectedPermission()
            });
            this.isSubmitting = false;
            if (!result.success) {
                this.errorMessage.set(result.message ?? DocumentsMessagingConstants.DOCUMENTS_CREATE_FOLDER_FAILED());
                return;
            }
            this.submitted.emit({ name: result.data!.name, mode: this.mode() });
            this.close();
            return;
        }

        const target = this.target();
        if (!target) {
            this.isSubmitting = false;
            return;
        }

        if (this.isFileMode()) {
            const result = await this.documentService.updateDocument(target.documentNodeId, {
                name: finalName + this.fileExtension()
            });
            this.isSubmitting = false;
            if (!result.success) {
                this.errorMessage.set(result.message ?? DocumentsMessagingConstants.DOCUMENTS_RENAME_FILE_FAILED());
                return;
            }
            this.submitted.emit({ name: result.data!.name, mode: this.mode() });
            this.close();
            return;
        }

        const result = await this.documentService.updateFolder(target.documentNodeId, {
            name: finalName,
            parentFolderId: target.parentId,
            permission: target.permission
        });
        this.isSubmitting = false;
        if (!result.success) {
            this.errorMessage.set(result.message ?? DocumentsMessagingConstants.DOCUMENTS_RENAME_FAILED());
            return;
        }
        this.submitted.emit({ name: result.data!.name, mode: this.mode() });
        this.close();
    }

    public cancel(): void {
        this.cancelled.emit();
        this.close();
    }

    public close(): void {
        this.bootstrapService.manageModal(`#${this.modalId}`, ModalActions.Hide);
    }
}
